import React, { memo, useState } from 'react';
import { LogOut, User, Menu, Crown, Search, X, Sun, Moon, HelpCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useColorMode } from '../../context/ColorModeContext';
import { useNavigate } from 'react-router-dom';
import SearchInput from '../common/SearchInput';
import { isPro as checkIsPro, getPlanLabel, getPlanBadgeClasses } from '../../utils/planPermissions';


interface HeaderProps {
  toggleSidebar: () => void;
}

// --- Badge del plan con shimmer para Premium ---
const PlanBadgeDesktop: React.FC<{ plan: string }> = ({ plan }) => {
  const isPremium = plan === 'premium';
  return (
    // Encabezado blanco desde el rediseño (8-oct-2026): el amarillo claro sobre amarillo pálido casi no
    // se leía. En claro va la pastilla navy de la marca con la corona dorada; en oscuro, el tono de siempre.
    <span className={`relative overflow-hidden ml-2 flex items-center text-xs font-semibold tracking-wide px-2 py-0.5 rounded-full ${
      isPremium
        ? 'bg-purple-100 text-purple-800 dark:bg-purple-400/20 dark:text-purple-300'
        : 'bg-[#0E1E3A] text-white dark:bg-yellow-400/15 dark:text-yellow-300'
    }`}>
      <Crown className={`h-3 w-3 mr-1 ${isPremium ? '' : 'text-amber-300'}`} />
      {getPlanLabel(plan as any)}
      {isPremium && (
        <span className="absolute inset-0 animate-shimmer rounded-full" />
      )}
    </span>
  );
};

const PlanBadgeMobile: React.FC<{ plan: string }> = ({ plan }) => {
  const isPremium = plan === 'premium';
  return (
    <span className={`relative overflow-hidden ml-2 inline-flex items-center text-xs px-2 py-0.5 rounded-full ${getPlanBadgeClasses(plan as any)}`}>
      <Crown className="h-3 w-3 mr-1" />
      {getPlanLabel(plan as any)}
      {isPremium && (
        <span className="absolute inset-0 animate-shimmer rounded-full" />
      )}
    </span>
  );
};

// Relanza el tour de bienvenida al panel (ManagerTour escucha este evento).
const startPanelTour = () => window.dispatchEvent(new CustomEvent('lealify:start-tour'));

// --- SUB-COMPONENTE: Toggle de modo claro/oscuro ---
const ColorModeToggle: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { mode, toggleMode } = useColorMode();
  const isDark = mode === 'dark';
  return (
    <button
      type="button"
      onClick={toggleMode}
      className={`flex items-center justify-center rounded-full p-2 text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30 transition-colors ${className}`}
      aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      title={isDark ? 'Modo claro' : 'Modo oscuro'}
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </button>
  );
};

// --- SUB-COMPONENTE: Sección de Usuario (Escritorio) ---
const UserInfoSection: React.FC<{ user: any; hasProAccess: boolean; onLogout: () => void }> = ({ user, hasProAccess, onLogout }) => {
  // 10-oct-2026: `user.role` es 'manager' para TODO usuario de negocio (dueño y empleado decían «Gerente»);
  // el rol real vive en baseRole.
  const getRoleDisplayName = (role: string | undefined): string => {
    if (role === 'admin') return 'Administrador';
    if (user?.baseRole === 'owner') return 'Dueño(a)';
    if (user?.baseRole === 'staff') return 'Empleado';
    if (role === 'manager') return 'Gerente';
    return 'Usuario';
  };

  return (
    <div className="hidden md:flex items-center">
      <div className="flex items-center space-x-4">
        {user?.role !== 'admin' && (
          <button
            type="button"
            onClick={startPanelTour}
            className="flex items-center justify-center rounded-full p-2 text-content-2 hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-accent/30 transition-colors"
            aria-label="Conoce tu panel (recorrido guiado)"
            title="Conoce tu panel"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
        )}
        <ColorModeToggle className="!text-content-2 hover:!bg-surface-2" />
        <div className="flex items-center space-x-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/10"><User className="h-5 w-5 text-accent" aria-hidden="true" /></span>
          <div className="flex flex-col">
            <div className="flex items-center">
              <span className="text-sm font-semibold text-content">
                {user?.displayName ?? 'Usuario'}
              </span>
              {hasProAccess && (
                <PlanBadgeDesktop plan={user?.subscription?.plan || 'pro'} />
              )}
            </div>
            <span className="text-xs text-content-3">
              {getRoleDisplayName(user?.role)}
            </span>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="inline-flex items-center px-4 py-1.5 border border-line text-sm leading-5 font-medium rounded-full text-content-2 hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-accent/30 transition-colors duration-150"
          aria-label="Cerrar sesión"
        >
          <LogOut className="h-4 w-4 mr-1" aria-hidden="true" />
          <span>Salir</span>
        </button>
      </div>
    </div>
  );
};

// --- SUB-COMPONENTE: Menú Móvil ---
const MobileMenu: React.FC<{ user: any; hasProAccess: boolean; onLogout: () => void; onClose: () => void }> = ({ user, hasProAccess, onLogout, onClose }) => {
  // 10-oct-2026: `user.role` es 'manager' para TODO usuario de negocio (dueño y empleado decían «Gerente»);
  // el rol real vive en baseRole.
  const getRoleDisplayName = (role: string | undefined): string => {
    if (role === 'admin') return 'Administrador';
    if (user?.baseRole === 'owner') return 'Dueño(a)';
    if (user?.baseRole === 'staff') return 'Empleado';
    if (role === 'manager') return 'Gerente';
    return 'Usuario';
  };

  return (
    <>
      {/* Backdrop invisible - cierra el menú al tocar fuera */}
      <div
        className="fixed inset-0 z-40 md:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Menú */}
      <div className="absolute top-16 right-0 left-0 z-50 md:hidden bg-surface shadow-lg border-t border-line animate-fade-in-up">
        <div className="p-4">
          <div className="flex flex-col space-y-5">
            {/* User Information */}
            <div className="flex items-center space-x-3 pb-4 border-b border-line">
              <div className="flex-shrink-0 p-2 rounded-full bg-blue-100 dark:bg-blue-500/20">
                <User className="h-6 w-6 text-accent" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-content">
                  {user?.displayName ?? 'Usuario'}
                  {hasProAccess && (
                    <PlanBadgeMobile plan={user?.subscription?.plan || 'pro'} />
                  )}
                </h3>
                <p className="text-sm text-content-3">
                  {getRoleDisplayName(user?.role)}
                </p>
              </div>
            </div>
            
            {/* Business Name */}
            {user?.businessName && (
              <div className="flex items-center justify-between pt-2 pb-4 border-b border-line">
                <h3 className="text-sm font-medium text-content-3">Negocio:</h3>
                <p className="text-base font-medium text-content">
                  {user.businessName}
                </p>
              </div>
            )}

            {/* Conoce tu panel (relanza el tour de bienvenida) */}
            {user?.role !== 'admin' && (
              <button
                onClick={() => { onClose(); startPanelTour(); }}
                className="w-full flex justify-center items-center py-3 border border-line text-base font-medium rounded-md text-content bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors"
              >
                <HelpCircle className="h-5 w-5 mr-2" aria-hidden="true" />
                Conoce tu panel
              </button>
            )}

            {/* Logout Button */}
            <button
              onClick={onLogout}
              className="w-full flex justify-center items-center py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm transition-colors"
            >
              <LogOut className="h-5 w-5 mr-2" aria-hidden="true" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>
    </>
  );
};


// --- COMPONENTE PRINCIPAL ---

const Header: React.FC<HeaderProps> = memo(({ toggleSidebar }) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Check if user is Pro or Premium
  const hasProAccess = checkIsPro(currentUser?.subscription);
                
  // Check if user is admin
  const isAdmin = currentUser?.role === 'admin';

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('[Header] Error during logout:', error);
    }
  };

  const handleSearch = (term: string | null) => {
    if (term?.trim()) {
      setShowSearch(false);
    }
  };

  // Rediseño «Marca» (8-oct-2026): navy en celular (como la barra), blanco en escritorio; el logo de escritorio vive en la barra lateral.
  return (
    <header className="relative z-30 flex-shrink-0 bg-[#0E1E3A] shadow-lg md:bg-surface md:shadow-none md:border-b md:border-line">
      <div className="w-full px-0 sm:px-6 lg:px-8 2xl:px-10">
        <div className="flex justify-between h-16">
          {/* Left side with menu button and logo */}
          <div className="flex items-center">
            <button
              type="button"
              className="md:hidden px-4 text-white/90 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white/30 transition-colors"
              onClick={toggleSidebar}
              aria-label="Abrir menú lateral"
            >
              <span className="sr-only">Abrir menu</span>
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>
            
            <div className="flex items-center pl-4 md:hidden">
              <svg
                viewBox="0 0 300 120"
                className="h-8 w-auto"
                style={{ fill: '#ffffff' }}
                aria-hidden="true"
              >
                <path d="M262.5,120,272.21,98.96,249.435,44.573h14.586l15.4,38.445L294.6,44.573h14.044L276.764,120ZM146.57,94.406l-.326-1.3c-3.632,6.29-8.838,10.3-16.809,10.3-10.845,0-17.949-6.073-17.949-15.617,0-9.6,6.182-14.478,18.707-17.027l11.062-2.223c3.416-.651,4.989-2.006,4.989-5.314,0-5.206-3.687-8.405-9.868-8.405-6.4,0-9.869,2.874-10.737,7.537L113,61.492c1.193-9.977,9.869-18.166,23.913-18.166,9.382,0,16.484,3.361,19.684,9.327,1.572,2.983,2.44,6.561,2.44,12.851V88.766c0,2.332.977,3.416,2.712,3.525a12.729,12.729,0,0,0,2.006-.109V102.92a27.663,27.663,0,0,1-4.772.542C152.644,103.461,148.035,100.371,146.57,94.406Zm-6.127-17.352-7.538,2.007c-6.4,1.68-8.73,4.446-8.73,7.862,0,3.741,2.982,5.639,7.753,5.639,6.833,0,14.37-4.664,14.37-15.075V74.831C145.106,75.59,143.968,76.078,140.443,77.054ZM50.592,73.366c0-18,13.177-30.094,28.143-30.094,16.972,0,27.492,12.959,27.6,29.173a38.53,38.53,0,0,1-.325,4.989h-42.4c.868,8.3,7.213,14.261,16.16,14.261,6.453,0,10.519-3.2,12.526-7.537L104.6,85.946c-3.2,10.52-12.254,17.407-24.781,17.407C62.467,103.353,50.592,91.641,50.592,73.366Zm13.123-5.531H93.1c-1.355-7.646-6.127-12.851-14.532-12.851C71.252,54.985,65.179,60.081,63.715,67.836Zm144.13,34.216H170.592V25.54h12.8V89.417h11.875V44.573h12.743v57.479ZM0,102.052V0H43.543V76.512H30.2v-64.2H13.394V89.743H43.543v12.309ZM225.034,102V56.014H214.46v-11.5h10.628v-2.44c0-10.085,5.693-17.84,17.3-17.84a27.308,27.308,0,0,1,7.429.868l-1.193,11.5a16.918,16.918,0,0,0-4.555-.434c-4.066,0-6.29,2.006-6.29,6.4v2.007H248.4V56.014H237.776V102Zm-30.8-70.059a7.429,7.429,0,1,1,7.429,7.43A7.429,7.429,0,0,1,194.234,31.938Z" />
              </svg>
              <span className="ml-2 text-xs font-medium text-white/90 bg-black/20 px-2 py-1 rounded">
                {import.meta.env.VITE_APP_VERSION}
              </span>
            </div>

            {/* Mobile actions */}
            <div className="flex items-center ml-auto md:hidden">
              <ColorModeToggle />
              {!isAdmin && (
                <button 
                  className="p-2 text-white" 
                  onClick={() => {
                    setShowSearch(!showSearch);
                    setShowMobileMenu(false);
                  }}
                  aria-label={showSearch ? "Cerrar búsqueda" : "Abrir búsqueda"}
                >
                  {showSearch ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
                </button>
              )}
              
              <button 
                className="p-2 text-white" 
                onClick={() => {
                  setShowMobileMenu(!showMobileMenu);
                  setShowSearch(false);
                }}
                aria-label="Menú de usuario"
              >
                <User className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Search Bar (Desktop) */}
          {!isAdmin && (
            <div className="hidden md:flex items-center flex-1 max-w-xl mr-auto">
              <SearchInput
                avisoPro="tooltip"
                onSearch={handleSearch}
                placeholder=""
                className="w-full"
                minSearchLength={2}
                showResults={true}
                respectEmptyPlaceholder={true}
              />
            </div>
          )}

          {/* User Info (Desktop) */}
          <UserInfoSection user={currentUser} hasProAccess={hasProAccess || false} onLogout={handleLogout} />
        </div>

        {/* Search Bar (Mobile) */}
        {showSearch && !isAdmin && (
          <div className="md:hidden py-3 px-1 pb-4 animate-fade-in-up">
            <SearchInput
              avisoPro="tooltip"
              onSearch={handleSearch}
              placeholder=""
              className="w-full"
              minSearchLength={2}
              showResults={true}
              respectEmptyPlaceholder={true}
            />
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <MobileMenu
          user={currentUser}
          hasProAccess={hasProAccess || false}
          onLogout={handleLogout}
          onClose={() => setShowMobileMenu(false)}
        />
      )}
    </header>
  );
});

Header.displayName = 'Header';

export default Header;